'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  FINDING_CVSS_VERSIONS,
  getFindingCvssDisplayValues,
  resolveFindingCvssVersionRecord,
  type CvssEnvironmentalRequirements,
  type CvssScoreStatus,
  type FindingCvssFields,
  type FindingCvssVersionKey,
  type FindingCvssVersionSource,
  type FindingType,
} from '@terra/shared-cvss';
import { Badge } from '../../components/badge';
import {
  BodyDefault,
  BodyStrong,
  HeadingItem,
  HeadingPage,
  LabelDefault,
} from '../../components/typography';
import { FindingCvssEnvironmentalPanel } from './FindingCvssEnvironmentalPanel';
import { FindingCvssScoreCard } from './FindingCvssScoreCard';
import { FindingCvssVectorStrip } from './FindingCvssVectorStrip';
import { resolveFindingCvssEffectiveScore } from './finding-cvss-effective-score';

const EMPTY_TEXT = '—';
const SEVERITY_SOURCE_VERSION: FindingCvssVersionKey = '4.0';

type FindingCvssTabRecord = FindingCvssFields & {
  explanation?: string | null;
  metricReasons?: { metric: string; value: string; reason: string }[];
};

type FindingCvssTabSource = FindingCvssVersionSource<FindingCvssTabRecord> & {
  cvssScoreStatus?: CvssScoreStatus | null;
  findingType?: FindingType | null;
  findingTypeReason?: string | null;
};

export type FindingCvssTabProps = {
  finding: FindingCvssTabSource;
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  applicationCvssVersion?: FindingCvssVersionKey | null;
};

function FindingCvssTabVersion({
  version,
  record,
  score,
  environmentalRequirements,
}: {
  version: FindingCvssVersionKey;
  record: FindingCvssTabRecord | null;
  score: string | null;
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
}) {
  const t = useTranslations('designSystem.findings.cvssVersions');

  return (
    <VStack
      gap={5}
      alignItems="stretch"
      w="full"
      p={5}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <HStack gap={3} alignItems="center">
        <HeadingPage>{t('versionTitle', { version })}</HeadingPage>
        <Badge variant="outline" size="md">
          {score ?? t('notScored')}
        </Badge>
      </HStack>
      <FindingCvssVectorStrip
        version={version}
        vector={getFindingCvssDisplayValues(record).vector}
        metricReasons={record?.metricReasons ?? []}
      />
      <FindingCvssEnvironmentalPanel
        version={version}
        vector={getFindingCvssDisplayValues(record).vector}
        requirements={environmentalRequirements}
      />
      <VStack
        gap={2}
        alignItems="stretch"
        p={4}
        borderRadius="md"
        bg="bg.subtle"
        borderLeftWidth="4px"
        borderLeftColor="border.accent.default"
      >
        <LabelDefault color="fg.muted">{t('explanationTitle')}</LabelDefault>
        <BodyDefault>{record?.explanation || EMPTY_TEXT}</BodyDefault>
      </VStack>
    </VStack>
  );
}

export function FindingCvssTab({
  finding,
  environmentalRequirements,
  applicationCvssVersion,
}: FindingCvssTabProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const isPending = finding.cvssScoreStatus === 'PENDING';
  const records = FINDING_CVSS_VERSIONS.map((version) => {
    const record = resolveFindingCvssVersionRecord({ finding, version });
    const requirements =
      version === applicationCvssVersion ? environmentalRequirements : null;

    return {
      version,
      record,
      requirements,
      effective: resolveFindingCvssEffectiveScore({
        version,
        record,
        requirements,
      }),
    };
  });

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <HStack gap={4} alignItems="stretch" flexWrap="wrap">
        {finding.findingType ? (
          <VStack
            gap={2}
            alignItems="flex-start"
            flex="1 1 240px"
            minW="240px"
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border.subtle"
          >
            <HeadingItem>{t('findingTypeLabel')}</HeadingItem>
            <Badge variant="emphasis" size="md">
              {t(`findingTypes.${finding.findingType}`)}
            </Badge>
            {finding.findingTypeReason ? (
              <BodyDefault color="fg.muted">
                {finding.findingTypeReason}
              </BodyDefault>
            ) : null}
          </VStack>
        ) : null}
        {isPending
          ? null
          : records.map(({ version, effective }) => (
              <FindingCvssScoreCard
                key={version}
                version={version}
                score={effective.score}
                baseScore={effective.baseScore}
                isEnvironmental={effective.isEnvironmental}
                setsSeverity={version === SEVERITY_SOURCE_VERSION}
              />
            ))}
      </HStack>
      {isPending ? (
        <Box
          p={4}
          borderRadius="lg"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.default"
        >
          <BodyStrong color="fg.muted">{t('tabPending')}</BodyStrong>
        </Box>
      ) : (
        records.map(({ version, record, requirements, effective }) => (
          <FindingCvssTabVersion
            key={version}
            version={version}
            record={record}
            score={effective.score}
            environmentalRequirements={requirements}
          />
        ))
      )}
    </VStack>
  );
}
